/**
 * Life4Earth V2 — Mini-Project Tasks API client.
 *
 * DEV-1179 (2026-06-03). Two client functions used by the Engagements
 * page's Tasks tab to surface mini-project tasks for the current user:
 *   - fetchMyMiniProjectTasks: caller's open tasks on a given MP
 *   - transitionMiniProjectTaskStatus: Mark Done / In Progress / Reopen
 *
 * Backend lives in MiniProjectTasksController. Auth: the GET requires
 * CanReadMiniProject; the transition uses CanUpdateOwnTaskStatus
 * (Lead / sysadmin OR direct OwnerUserGuid OR active role-assignment
 * holder for the task's AssignedRoleGuid).
 *
 * All calls go through apiFetch which handles base URL, Bearer JWT,
 * and X-User-Guid fallback for dev. Errors throw ApiError.
 */

import { apiFetch } from "./client";
import { dispatchMiniProjectMutated } from "./miniProjects";

/**
 * Wire-shape mirroring MiniProjectTaskResponse on the server. Only the
 * fields the Engagements Tasks tab consumes are typed; additional
 * server fields pass through unchanged.
 */
export interface MiniProjectTask {
  taskGuid: string;
  miniProjectGuid: string;
  playbookTaskKey?: string | null;
  nameKey?: string | null;
  nameText?: string | null;
  description?: string | null;
  status: "not_started" | "in_progress" | "done";
  ownerUserGuid?: string | null;
  assignedRoleGuid?: string | null;
  dueDate?: string | null; // YYYY-MM-DD (DateOnly)
  notes?: string | null;
  howToUrl?: string | null;
  evidenceAttachmentKeys?: string[];
  instructionSteps?: string | null;
  requiresEvidence: boolean;
  position: number;
  isTemplateTask: boolean;
  /** DEV-1285. True for platform-seeded tasks (warm-start "Project initiated"
   *  milestone). Distinct from isTemplateTask (Playbook origin). */
  isSystemTask: boolean;
  createdAt: string;
  actualStartAt?: string | null;
  completedAt?: string | null;
  /** DEV-1257. Optional completion note captured when the assignee marked
   *  the task done from the Action Center tray. NULL when no note. */
  completionNote?: string | null;
  updatedAt: string;
}

/**
 * DEV-1257. Wire-shape for the cross-MP "my open tasks" feed used by the
 * Action Center inbox. Mirrors MyMiniProjectTaskResponse on the server.
 * Nests the full task plus the parent MP display name (Source column).
 */
export interface MyMiniProjectTask {
  task: MiniProjectTask;
  miniProjectName: string;
  /** Owning community of the parent mini-project (SPEC_ROLE_ORGANIZED_INBOX §4a)
   *  — lets the Inbox group project tasks under their org. May be null. */
  ownerCommunityGuid?: string | null;
  ownerCommunityName?: string | null;
}

/**
 * GET /api/mini-projects/{guid}/tasks/mine
 *
 * Returns the caller's open (not_started / in_progress) tasks on the
 * given mini-project — both direct (OwnerUserGuid = caller) and
 * role-anchored (AssignedRoleGuid matches an active role assignment).
 *
 * Server-ordered: due_date asc (nulls last), then position, then
 * created_at. Caller may apply further bucketing client-side.
 */
export async function fetchMyMiniProjectTasks(
  miniProjectGuid: string,
): Promise<MiniProjectTask[]> {
  return apiFetch(
    `/api/mini-projects/${miniProjectGuid}/tasks/mine`,
  );
}

/**
 * POST /api/mini-projects/{guid}/tasks/{taskGuid}/transition
 *
 * Transitions a task's status. Valid: not_started ↔ in_progress ↔ done.
 * Server enforces RequiresEvidence (≥1 attachment for done) and the
 * widened auth (DEV-1179 Q3=A: Lead OR own-task assignee).
 *
 * Dispatches EVT_MINI_PROJECT_MUTATED on success so any portfolio-level
 * MP consumer refetches; the Engagements Tasks tab caller should also
 * bump its local nonce to drop the row off the immediate-items list.
 */
export async function transitionMiniProjectTaskStatus(
  miniProjectGuid: string,
  taskGuid: string,
  newStatus: "not_started" | "in_progress" | "done",
  /** DEV-1257. Optional completion note. Server persists only when
   *  newStatus === "done"; ignored on other transitions. */
  completionNote?: string,
  /** DEV-1257. Optional new evidence storage keys to APPEND to the task's
   *  existing array as part of this transition. Lets the Action Center
   *  "Done" flow do upload + attach + complete atomically. Append-only;
   *  never clears existing keys. The server counts existing + new toward
   *  the RequiresEvidence gate. */
  newEvidenceKeys?: string[],
): Promise<MiniProjectTask> {
  const body: Record<string, unknown> = { newStatus };
  if (newStatus === "done" && completionNote && completionNote.trim()) {
    body.completionNote = completionNote.trim();
  }
  if (newEvidenceKeys && newEvidenceKeys.length > 0) {
    body.newEvidenceKeys = newEvidenceKeys;
  }
  const result = await apiFetch<MiniProjectTask>(
    `/api/mini-projects/${miniProjectGuid}/tasks/${taskGuid}/transition`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
  dispatchMiniProjectMutated(miniProjectGuid);
  return result;
}

/**
 * DEV-1257-followup. POST /api/mini-projects/{guid}/tasks/{taskGuid}/evidence/add
 *
 * Append one or more evidence storage keys to the task. Used by the
 * Action Center tray's upload-on-pick flow — the tray calls uploadFile
 * (FileStorageController → MinIO) to land the bytes, then immediately
 * calls this endpoint to persist the storage keys. Server filters dupes
 * and empties; safe to call repeatedly.
 *
 * Auth: CanUpdateOwnTaskStatus (widened — assignees can attach their own
 * evidence, not only Leads). Returns the updated task.
 */
export async function addMiniProjectTaskEvidence(
  miniProjectGuid: string,
  taskGuid: string,
  keys: string[],
): Promise<MiniProjectTask> {
  const result = await apiFetch<MiniProjectTask>(
    `/api/mini-projects/${miniProjectGuid}/tasks/${taskGuid}/evidence/add`,
    {
      method: "POST",
      body: JSON.stringify({ keys }),
    },
  );
  dispatchMiniProjectMutated(miniProjectGuid);
  return result;
}

/**
 * DEV-1289. Toggle a single instruction step's checked state. Assignee-
 * permitted (CanUpdateOwnTaskStatus) — only the matched step's `checked`
 * flag changes; labels/links/required stay Lead-authored. Returns the task.
 */
export async function toggleMiniProjectTaskStep(
  miniProjectGuid: string,
  taskGuid: string,
  index: number,
  checked: boolean,
): Promise<MiniProjectTask> {
  // NOTE: intentionally does NOT call dispatchMiniProjectMutated. A step tick
  // is an in-place edit inside the open detail tray; the global mutation
  // broadcast triggers a cache invalidation that collapses the tray. The
  // caller refreshes the local view via its own onMutated (same pattern the
  // edit-tray status pill uses). DEV-1289 follow-up.
  return apiFetch<MiniProjectTask>(
    `/api/mini-projects/${miniProjectGuid}/tasks/${taskGuid}/steps/toggle`,
    {
      method: "POST",
      body: JSON.stringify({ index, checked }),
    },
  );
}

/**
 * DEV-1257-followup. POST /api/mini-projects/{guid}/tasks/{taskGuid}/evidence/remove
 *
 * Remove a single evidence storage key from the task. Auth widened
 * to CanUpdateOwnTaskStatus so assignees can fix their own mistakes
 * (not just the Lead). Idempotent — removing a missing key is a no-op.
 * Returns the updated task.
 *
 * NOTE: this does NOT delete the underlying object in MinIO. Storage
 * cleanup is a separate concern handled by S3 lifecycle policies.
 */
export async function removeMiniProjectTaskEvidence(
  miniProjectGuid: string,
  taskGuid: string,
  key: string,
): Promise<MiniProjectTask> {
  const result = await apiFetch<MiniProjectTask>(
    `/api/mini-projects/${miniProjectGuid}/tasks/${taskGuid}/evidence/remove`,
    {
      method: "POST",
      body: JSON.stringify({ key }),
    },
  );
  dispatchMiniProjectMutated(miniProjectGuid);
  return result;
}

/**
 * DEV-1257. GET /api/action-items/mini-project-tasks
 *
 * Cross-MP feed: every open task that's mine (direct owner OR active
 * role assignment) across every mini-project I can read. Powers the
 * "Task" rows in the Action Center inbox — the per-MP fetchMyMiniProjectTasks
 * above is only callable when an MP is already in context.
 *
 * Server-ordered: due_date asc (nulls last), then position, then created_at.
 */
export async function fetchMyOpenMiniProjectTasks(): Promise<MyMiniProjectTask[]> {
  return apiFetch<MyMiniProjectTask[]>("/api/action-items/mini-project-tasks");
}

